import { InitialUserState } from '../datatypes/AppState';
import { UserInfoUpdate } from '../datatypes/UserInfoUpdate';
import axiosService from '../services/axios.service';
import { localStorageUtils } from '../utils/storageUtils';

const authApi = () => {
  const { getGuestHttpClient, getUserHttpClient } = axiosService();

  const storeUserState = (response: any) => {
    const userState: InitialUserState = {
      isLoggedIn: true,
      user: response.data.user,
      lastAnsweredPage: response.data.lastAnsweredPage,
      userWillStatus: response.data.userWillStatus,
      hasLivingWill: response.data.hasLivingWill,
    };
    localStorage.setItem('userState', JSON.stringify(userState));
    if (response.data.access_token) {
      localStorage.setItem(
        'attorney_registration_token',
        response.data.access_token,
      );
      localStorage.setItem('token_expiry', response.data.expires_in);
    }
  };

  const register = async (registerData: any) => {
    return await getGuestHttpClient()
      .post('/register', registerData)
      .then((response: any) => {
        return {
          success: true,
          message: response.data.message,
          errors: response.data.errors,
        };
      })
      .catch((error) => {
        return {
          success: false,
          message: error?.response?.data?.message || error?.message,
          errors: error?.response?.data?.errors,
        };
      });
  };

  const authenticate = async (loginData: any) => {
    return await getGuestHttpClient()
      .post('/login', {
        ...loginData,
      })
      .then((response) => response.data)
      .catch((error) => error?.response?.data ?? error);
  };

  const verifyOnetimeCode = async (loginData: any) => {
    return await getGuestHttpClient()
      .post('verify-onetime-code', { ...loginData })
      .then((response) => {
        storeUserState(response);
        return {
          success: true,
          user: response.data.user,
        };
      })
      .catch(() => {
        return {
          success: false,
          user: null,
        };
      });
  };

  const resendOneTimeCode = async (email: string) => {
    return await getGuestHttpClient()
      .post('resend-onetime-code', { email })
      .then((response) => response.data)
      .catch((error) => error?.response?.data ?? error);
  };

  const activateAccount = async (token: string) => {
    return await getGuestHttpClient()
      .post(`/verify/${token}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        if (response.data.success) {
          storeUserState(response);
          return {
            successMessage: response.data.message,
            errorMessage: '',
          };
        }
        return {
          successMessage: '',
          errorMessage: response.data.message,
        };
      })
      .catch((error) => {
        if (error && error.response) {
          return {
            successMessage: '',
            errorMessage: error.response.data.message,
          };
        }
      });
  };

  const sendResetPasswordLink = async (email: string) => {
    return await getGuestHttpClient()
      .post('/reset-password-link', { email })
      .then((response) => {
        if (response.data.success) {
          return {
            success: true,
            message: response.data.message,
          };
        }
        return {
          success: false,
          message: response.data.message,
        };
      })
      .catch((error) => {
        console.log(error);
        if (error && error.response) {
          return {
            success: false,
            message: error.response.data.message,
          };
        }
      });
  };

  const sendVerificationLink = async (email: string) => {
    return await getGuestHttpClient()
      .post('/resend-token', { email })
      .then((response) => {
        if (response.data.success) {
          return {
            success: true,
            message: response.data.message,
          };
        }
        return {
          success: false,
          message: response.data.message,
        };
      })
      .catch((error) => {
        console.log(error);
        if (error && error.response) {
          return {
            success: false,
            message: error.response.data.message,
          };
        }
      });
  };

  const resetPassword = async (resetPasswordData: {
    email: string;
    token: string;
    password: string;
  }) => {
    return await getGuestHttpClient()
      .post('/reset-password', {
        email: resetPasswordData.email,
        token: resetPasswordData.token,
        password: resetPasswordData.password,
      })
      .then((response) => {
        if (response.status === 200) {
          return {
            success: true,
            message: response.data.message,
          };
        }
        return {
          success: false,
          message: response.data.message,
        };
      })
      .catch((errors) => {
        return {
          success: false,
          message: errors.message,
          errors: errors?.response?.data?.message ?? errors,
        };
      });
  };

  const logout = async () => {
    return await getUserHttpClient({ responseType: 'json' }).post('/logout');
  };

  const updateProfile = async (userProfile: UserInfoUpdate) => {
    return await getUserHttpClient({ responseType: 'json' })
      .post(`/profile`, userProfile, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        if (response.data.success) {
          let userState: InitialUserState | null =
            localStorageUtils.getInitialUserState();
          if (userState) {
            userState = {
              ...userState,
              user: response.data.user,
            };
            localStorage.setItem('userState', JSON.stringify(userState));
          }
          return {
            success: true,
            user: response.data.user,
            errors: null,
            message: response.data.message,
          };
        }
      })
      .catch((error) => {
        return {
          success: false,
          user: null,
          errors: error.data.errors,
          message: error.data.message,
        };
      });
  };

  const refreshToken = async (refreshToken: string) => {
    return await getGuestHttpClient()
      .post('/refresh-token', { refreshToken })
      .then((response) => {
        if (response.data.success) {
          return {
            will_registration_token: response.data.access_token,
          };
        }
      });
  };

  const changePassword = async (data: any) => {
    return await getUserHttpClient({ responseType: 'json' })
      .post('/change-password', data)
      .then((response) => {
        if (response.data.success) {
          return {
            success: true,
            errors: null,
            message: response.data.message,
          };
        } else {
          return {
            success: false,
            errors: response.data?.errors,
            message: response.data.message,
          };
        }
      })
      .catch((error) => {
        return {
          success: false,
          errors: error.data.errors,
          message: error.data.message,
        };
      });
  };
  const getAccountDetails = async () => {
    return await getUserHttpClient({ responseType: 'json' })
      .get('/profile')
      .then((response) => {
        if (response.data.success) {
          const userState: InitialUserState = {
            isLoggedIn: true,
            user: response.data.user,
            hasLivingWill: response.data.hasLivingWill,
          };
          localStorage.setItem('userState', JSON.stringify(userState));
          return {
            success: true,
            user: userState.user,
          };
        }
        return {
          success: false,
          user: null,
        };
      });
  };

  const emailWillCertificate = async () => {
    return await getUserHttpClient({ responseType: 'json', accept: 'json' })
      .post('/living-will/send-certificate')
      .then((response) => response.data);
  };

  return {
    storeUserState,
    authenticate,
    logout,
    verifyOnetimeCode,
    resendOneTimeCode,
    activateAccount,
    refreshToken,
    sendResetPasswordLink,
    updateProfile,
    register,
    resetPassword,
    sendVerificationLink,
    changePassword,
    getAccountDetails,
    emailWillCertificate,
  };
};

export default authApi;
